import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Alert,
  Button,
  IconButton,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import apiClient from '../../services/api';

const WorkExperienceForm = ({ initialData = [], onSave, onCancel, loading = false }) => {
  const [workList, setWorkList] = React.useState(
    initialData.length > 0 ? initialData : [{}]
  );
  const [errors, setErrors] = React.useState({});
  const [saveError, setSaveError] = React.useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const [isDataModified, setIsDataModified] = React.useState(false);

  // 监听initialData变化，更新表单数据
  React.useEffect(() => {
    // 只有在数据没有被修改的情况下才更新表单数据
    if (!isDataModified) {
      setWorkList(initialData.length > 0 ? initialData : [{}]);
    }
  }, [initialData, isDataModified]);

  // 监听loading状态，当外部触发保存时自动执行保存逻辑
  React.useEffect(() => {
    if (loading) {
      // 使用 setTimeout 确保在下一个事件循环中执行，避免竞态条件
      const timeoutId = setTimeout(() => {
        handleSave();
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [loading]);

  // 处理表单字段变化
  const handleFormChange = (index, field, value) => {
    setWorkList(prev => {
      const newList = [...prev];
      newList[index] = {
        ...newList[index],
        [field]: value
      };
      return newList;
    });
    
    // 标记数据已被修改
    setIsDataModified(true);
    
    // 清除对应字段的错误
    const errorKey = `work.${index}.${field}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // 处理是否在职变化
  const handleCurrentJobChange = (index, checked) => {
    setWorkList(prev => {
      const newList = [...prev];
      newList[index] = {
        ...newList[index],
        isCurrent: checked
      };
      // 如果选择在职，清空结束日期
      if (checked) {
        newList[index].endDate = '';
      }
      return newList;
    });
    
    // 标记数据已被修改
    setIsDataModified(true);
  };

  // 添加工作经历
  const handleAddWork = () => {
    setWorkList(prev => [...prev, {}]);
    setIsDataModified(true);
  };

  // 删除工作经历
  const handleRemoveWork = (index) => {
    if (workList.length > 1) {
      setWorkList(prev => prev.filter((_, i) => i !== index));
      setIsDataModified(true);
      
      // 清除相关错误
      setErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith(`work.${index}.`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

  // 验证表单
  const validateForm = () => {
    const newErrors = {};
    
    workList.forEach((work, index) => {
      if (!work.companyName) {
        newErrors[`work.${index}.companyName`] = '请输入公司名称';
      }
      if (!work.position) {
        newErrors[`work.${index}.position`] = '请输入职位';
      }
      if (!work.startDate) {
        newErrors[`work.${index}.startDate`] = '请输入开始日期';
      }
      if (!work.isCurrent && !work.endDate) {
        newErrors[`work.${index}.endDate`] = '请输入结束日期';
      }
      if (!work.workType) {
        newErrors[`work.${index}.workType`] = '请选择工作类型';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 处理保存
  const handleSave = async () => {
    setSaveError('');
    
    if (!validateForm()) {
      // 验证失败时，显示错误提示
      setShowErrorSnackbar(true);
      // 通知父组件保存失败
      onSave && onSave(null);
      return;
    }

    try {
      const response = await apiClient.post('/api/application/update-work-experience', {
        workExperience: workList
      });
      
      if (response && response.success) {
        // 保存成功后，重置修改标记
        setIsDataModified(false);
        onSave && onSave(workList);
      } else {
        setSaveError(response?.message || '保存失败');
        setShowErrorSnackbar(true);
        onSave && onSave(null);
      }
    } catch (error) {
      console.error('保存工作经历失败:', error);
      setSaveError(error?.response?.data?.message || error.message || '保存失败');
      setShowErrorSnackbar(true);
      onSave && onSave(null);
    }
  };

  // 关闭错误提示
  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  return (
    <Box>
      {/* 移除固定位置的错误提示 */}
      
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
        工作经历信息
      </Typography>

      {workList.map((work, index) => (
        <Card key={index} sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                工作经历 {index + 1}
              </Typography>
              {workList.length > 1 && (
                <IconButton
                  color="error"
                  onClick={() => handleRemoveWork(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="公司名称 *"
                  value={work.companyName || ''}
                  onChange={(e) => handleFormChange(index, 'companyName', e.target.value)}
                  error={!!errors[`work.${index}.companyName`]}
                  helperText={errors[`work.${index}.companyName`]}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="职位 *"
                  value={work.position || ''}
                  onChange={(e) => handleFormChange(index, 'position', e.target.value)}
                  error={!!errors[`work.${index}.position`]}
                  helperText={errors[`work.${index}.position`]}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors[`work.${index}.workType`]}>
                  <InputLabel>工作类型 *</InputLabel>
                  <Select
                    value={work.workType || ''}
                    onChange={(e) => handleFormChange(index, 'workType', e.target.value)}
                    label="工作类型 *"
                  >
                    <MenuItem value="gov">政府机/法定机构</MenuItem>
                    <MenuItem value="self">自雇人士</MenuItem>
                    <MenuItem value="private">私人单位</MenuItem>
                  </Select>
                  {errors[`work.${index}.workType`] && (
                    <FormHelperText>{errors[`work.${index}.workType`]}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="开始日期 *"
                  type="date"
                  value={work.startDate || ''}
                  onChange={(e) => handleFormChange(index, 'startDate', e.target.value)}
                  error={!!errors[`work.${index}.startDate`]}
                  helperText={errors[`work.${index}.startDate`]}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="结束日期"
                  type="date"
                  value={work.endDate || ''}
                  onChange={(e) => handleFormChange(index, 'endDate', e.target.value)}
                  error={!!errors[`work.${index}.endDate`]}
                  helperText={errors[`work.${index}.endDate`]}
                  InputLabelProps={{ shrink: true }}
                  disabled={work.isCurrent}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={work.isCurrent || false}
                      onChange={(e) => handleCurrentJobChange(index, e.target.checked)}
                    />
                  }
                  label="至今仍在职"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="联系电话"
                  value={work.phone || ''}
                  onChange={(e) => handleFormChange(index, 'phone', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="手机号码"
                  value={work.mobilePhone || work.mobile || ''}
                  onChange={(e) => handleFormChange(index, 'mobilePhone', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="邮箱"
                  type="email"
                  value={work.email || ''}
                  onChange={(e) => handleFormChange(index, 'email', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="地址"
                  value={work.address || ''}
                  onChange={(e) => handleFormChange(index, 'address', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="邮政编码"
                  value={work.postalCode || ''}
                  onChange={(e) => handleFormChange(index, 'postalCode', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="工作内容描述"
                  multiline
                  rows={3}
                  value={work.description || ''}
                  onChange={(e) => handleFormChange(index, 'description', e.target.value)}
                  placeholder="请详细描述您的工作职责和成就..."
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
      
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddWork}
        sx={{ mb: 3 }}
      >
        添加工作经历
      </Button>

      {/* 浮动错误提示 */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseErrorSnackbar} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {saveError || '表单验证失败，请检查输入信息'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkExperienceForm; 